import styled from '@emotion/styled';
import { CleanHeader } from '@/components/ui/CleanHeader';
import { PolicyText } from '@/components/ui/PolicyText';
import { useNavigation } from '@/hooks/useNavigation';
import { PolicyBlock } from '@/types/policy';

const PolicyPage = styled.div`
  width: 100%;
  min-height: 100dvh;
  margin: 0 auto;
  padding: 16px;
  background: #ffffff;
`;

const SkipLink = styled.a`
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip-path: inset(50%);
  white-space: nowrap;

  &:focus {
    z-index: 1;
    width: auto;
    height: auto;
    padding: 8px 12px;
    clip-path: none;
    background: #ffffff;
    color: #155e75;
  }
`;

const Content = styled.main`
  width: 100%;
  max-width: 430px;
  margin: 24px auto 48px;
  color: #212121;
`;

const DocumentTitle = styled.h1`
  margin: 0 0 24px;
  font-size: 1.75rem;
  line-height: 1.2;
  font-weight: 700;
  letter-spacing: -0.02em;
`;

const BulletList = styled.ul`
  margin: 0 0 16px;
  padding-left: 24px;
  font-size: 1rem;
  line-height: 1.5;

  li {
    padding-left: 4px;
    margin-bottom: 6px;
  }
`;

interface PolicyPageLayoutProps {
  title: string;
  blocks: readonly PolicyBlock[];
}

export function PolicyPageLayout({ title, blocks }: PolicyPageLayoutProps) {
  const { goBack, canGoBack } = useNavigation();

  return (
    <PolicyPage>
      <SkipLink href="#policy-content">Skip to policy content</SkipLink>
      <CleanHeader title="Shieldix" showBack={canGoBack} onBack={goBack} />
      <Content id="policy-content" tabIndex={-1}>
        <DocumentTitle>{title}</DocumentTitle>
        {blocks.map((block, index) => {
          if (block.kind !== 'bullet') {
            return <PolicyText key={`${block.kind}-${index}`} block={block} />;
          }

          const isFirstBullet = index === 0 || blocks[index - 1]?.kind !== 'bullet';
          if (!isFirstBullet) {
            return null;
          }

          const bulletItems: PolicyBlock[] = [];
          for (let bulletIndex = index; blocks[bulletIndex]?.kind === 'bullet'; bulletIndex += 1) {
            const bullet = blocks[bulletIndex];
            if (bullet) {
              bulletItems.push(bullet);
            }
          }

          return (
            <BulletList key={`bullets-${index}`}>
              {bulletItems.map((bullet, bulletIndex) => (
                <li key={`${bullet.kind}-${index}-${bulletIndex}`}>
                  <PolicyText block={{ ...bullet, kind: 'body' }} />
                </li>
              ))}
            </BulletList>
          );
        })}
      </Content>
    </PolicyPage>
  );
}
